// Copyright (c) 2026 Michael F. Kamprath
//
// learn-tap.js -- Voice-on-tap counting for Learn mode.
//
// In Learn mode, tapping a countable object:
//   1. Checks the per-sprite `_counted` flag so each object counts
//      only once per scene.
//   2. Increments the scene-level counter.
//   3. Plays the counting voice audio for the new count value
//      (e.g. "three") drawn from the picked voice set.
//   4. Builds a number plaque via createNumberPlaque, attaches it to
//      the sprite container at the theme's centerPoint, and runs the
//      theme's entrance animator.
//   5. Pauses the sprite's idle animation while the plaque entrance
//      plays, then resumes it so the counted sprite continues to
//      behave like a member of the scene.
//
// Coordinate system: scene.js sizes the sprite container in design
// coords (spriteSize halved from @2x).  The plaque's centerPoint
// values in themes.json match the legacy iPad configuration, which
// uses @2x iPad pixel coords.  We halve centerPoint to position the
// plaque's VISUAL CENTER correctly within the design-coord sprite
// container.
//
// Teardown: on scene change or app shutdown, destroy() cancels every
// in-flight entrance animation, removes pointer listeners, detaches
// plaque children, and clears the _counted flag so zombie touches
// don't leak into the next scene.  Audio cancellation is handled by
// the app-level stopAllAudio() in the scene teardown path.

import { play as playOneShot } from '../audio/engine.js';
import { createNumberPlaque } from './number-plaque.js';
import { animatePlaqueEntrance, animatePlaqueReaction } from './plaque-animators.js';
import { resolveNumber } from '../audio/voice-set.js';
import { createCountModel } from './count-model.js';

// ---------------------------------------------------------------------------
// Position helpers
// ---------------------------------------------------------------------------

/**
 * Compute the plaque container's resting (x, y) in sprite-container
 * local coords, given the theme's numberPlaqueConfig.  centerPoint is
 * stored in @2x iPad pixel coords (matching legacy
 * `centerPoint~ipad`), so we halve it to get design coords -- the
 * same halving scene.js applies to spriteSize.
 *
 * This returns the UNFLIPPED baseline.  Callers that have a specific
 * sprite instance should mirror the x via `mirrorPlaqueXForFlip` when
 * the sprite's inner is flipped (scale.x < 0).
 *
 * Returns (0, 0) when the config lacks a centerPoint.
 *
 * @param {object} cfg  The theme's numberPlaqueConfig block.
 * @returns {{ x: number, y: number }}
 */
export function computePlaqueRestPosition(cfg) {
  const cp = cfg?.centerPoint;
  if (!cp) return { x: 0, y: 0 };
  return { x: cp.x / 2, y: cp.y / 2 };
}

/**
 * Mirror the plaque's resting x across the sprite container's
 * horizontal midline when the inner sprite is flipped.
 *
 * scene.js anchors the inner sprite at (0.5, 0.5) and flips it by
 * toggling `_inner.scale.x`.  The OUTER container's coord frame is
 * unaffected -- a plaque attached as a child of the outer container
 * therefore sits in un-mirrored coords.  The bucket/puff/etc. would
 * land on the wrong (non-flipped) side of the animal unless we mirror
 * the plaque's x to match.
 *
 * Only the container x is mirrored; the plaque's own contents
 * (numeral text, plaque image) are NOT flipped.
 *
 * @param {number} baseFinalX  Unflipped resting x (design coords).
 * @param {number} spriteW     Sprite container width (design coords).
 * @param {boolean} flipped    Whether the inner sprite is flipped.
 * @returns {number}
 */
export function mirrorPlaqueXForFlip(baseFinalX, spriteW, flipped) {
  if (!flipped || !Number.isFinite(spriteW) || spriteW <= 0) return baseFinalX;
  return spriteW - baseFinalX;
}

// ---------------------------------------------------------------------------
// Voice audio preload helper
// ---------------------------------------------------------------------------

/**
 * Build the list of voice-audio URLs the learn scene may need -- the
 * number recordings for 1..N where N is sprite count.  Unresolved
 * entries (voice set missing a template) are dropped so preload never
 * hits a 404.
 *
 * @param {object} opts
 * @param {object} opts.voiceSetsData   Parsed voice-sets.json.
 * @param {string} opts.locale          e.g. 'en'.
 * @param {object} opts.voiceSet        A picked voice set object.
 * @param {number} opts.maxNumber       The highest count we'd need.
 * @param {string} [opts.numeralScript] Defaults to 'arabic'.
 * @returns {string[]}
 */
export function collectLearnVoiceUrls({
  voiceSetsData,
  locale,
  voiceSet,
  maxNumber,
  numeralScript = 'arabic',
}) {
  if (!voiceSet || !Number.isFinite(maxNumber) || maxNumber < 1) return [];
  const urls = [];
  for (let n = 1; n <= maxNumber; n++) {
    const u = resolveNumber(voiceSetsData, locale, voiceSet, n, numeralScript);
    if (u) urls.push(u);
  }
  return urls;
}

// ---------------------------------------------------------------------------
// Tap manager
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} LearnTapManager
 * @property {() => number} getCount            Current scene count.
 * @property {() => void}   destroy             Tear down listeners + animations.
 */

/**
 * Create a Learn-mode tap manager for the scene's countable objects.
 *
 * @param {object} opts
 * @param {Array}  opts.sprites             Object containers from scene.js.
 * @param {object} opts.theme               Theme entry (with numberPlaqueConfig).
 * @param {object} opts.voiceSetsData       Parsed voice-sets.json.
 * @param {string} opts.locale              Locale string (e.g. 'en').
 * @param {object} opts.voiceSet            Picked voice set object.
 * @param {string} [opts.numeralScript]     Defaults to 'arabic'.
 * @param {object} [opts.plaqueAnimations]  animations.json plaqueAnimations block.
 * @param {Map|Object} [opts.plaqueTextureMap]  Pre-loaded plaque textures
 *                                              (static image + animator keyframes).
 * @param {object} [opts.idleManager]       Scene idle manager for pause/resume.
 * @param {object} [opts.objectReactions]   animations.json objectReactions block,
 *                                          used to drive the optional sprite-side
 *                                          reaction animation (e.g. worriedApple).
 * @param {Map|Object} [opts.reactionTextureMap]  Pre-loaded reaction textures
 *                                                (apple_pop_01..03, etc.).
 * @param {Set<object>|Iterable<object>} [opts.pendingSprites]
 *   Sprites that were tapped BEFORE the manager was constructed (during
 *   the post-entrance preload gap).  After registering pointerdown
 *   handlers, the manager replays each pending sprite's handler so the
 *   first tap registers immediately rather than being lost.
 * @param {import('./count-model.js').CountModel} [opts.countModel]
 *   Optional pre-built count model.  The state machine injects one so
 *   it has a single source of truth for the round's count; if omitted,
 *   the manager creates its own internal model for standalone use
 *   (existing tests, ad-hoc scenes).
 * @param {() => void} [opts.onRoundComplete]
 *   Optional "round narration complete" callback.  Fires on the LAST
 *   tap of the round, AFTER both the plaque entrance animation and
 *   the verbal number sound ("...three!") have finished.  Distinct
 *   from countModel.onComplete (which fires synchronously the moment
 *   the count hits the target): the state machine subscribes to
 *   onRoundComplete so its "Good counting!" praise doesn't step on
 *   the final number's voice or the plaque animation tail.
 * @param {() => boolean} [opts.verbalPromptsEnabled]
 *   Probe for the "Verbal prompts" setting.  When it returns `false`
 *   at tap time, the number voice ("one", "two", ...) is silenced.
 *   Plaque animations + object touch reactions still fire.  Typically
 *   `getVerbalPrompts` from src/settings.js.  Defaults to `() => true`
 *   so existing callers that don't pass it don't silently lose sound.
 * @returns {LearnTapManager}
 */
export function createLearnTapManager({
  sprites,
  theme,
  voiceSetsData,
  locale,
  voiceSet,
  numeralScript = 'arabic',
  plaqueAnimations = null,
  plaqueTextureMap = null,
  idleManager = null,
  objectReactions = null,
  reactionTextureMap = null,
  pendingSprites = null,
  countModel = null,
  onRoundComplete = null,
  verbalPromptsEnabled,
}) {
  const verbalOn = typeof verbalPromptsEnabled === 'function'
    ? verbalPromptsEnabled
    : () => true;
  const cfg = theme?.numberPlaqueConfig;
  if (!cfg || !Array.isArray(sprites) || sprites.length === 0) {
    return { getCount: () => 0, destroy() {} };
  }
  // Use the injected count model when present, otherwise spin up a
  // local one matching the sprite count.  Either way, the tap handler
  // treats it as the single source of truth for the round's count.
  const model = countModel || createCountModel({ objectCount: sprites.length });
  // Theme-level reaction config (the legacy displayPlaqueReactionConfig).
  // Null when the theme doesn't declare one, in which case the
  // sprite stays on its idle/static texture during the plaque reveal.
  const reactionCfg = theme?.plaqueReaction || null;

  // Baseline resting position assumes the sprite is NOT flipped.  The
  // per-sprite handler mirrors x via `mirrorPlaqueXForFlip` at tap
  // time, since flip state is per-sprite and can differ within a scene.
  const { x: baseFinalX, y: finalY } = computePlaqueRestPosition(cfg);

  let destroyed = false;
  /** @type {Set<{cancelled: boolean}>} */
  const inFlight = new Set();
  /** @type {Array<{sprite: any, handler: () => void}>} */
  const handlers = [];

  for (let i = 0; i < sprites.length; i++) {
    const sprite = sprites[i];
    const index = i;

    const handler = () => {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.log(`[learn-tap] handler fired for sprite ${index}`
          + ` (destroyed=${destroyed}, counted=${sprite._counted},`
          + ` reacting=${sprite._reacting})`);
      }
      if (destroyed) return;
      // One-shot: each sprite counts exactly once per scene.
      if (sprite._counted) return;
      // Also guard against the sprite being mid-reaction (learn mode
      // doesn't normally enter that state, but a shared guard keeps
      // behaviour predictable if external systems raise the flag).
      if (sprite._reacting) return;

      sprite._counted = true;
      // Mark _reacting so idle animations don't stomp the plaque
      // container we're about to attach.
      sprite._reacting = true;

      // Advance the round's count.  increment() returns the new value
      // or null if the model is already complete / destroyed, in which
      // case this tap is a late arrival we should silently drop.  It
      // also fires onComplete when count reaches objectCount, which is
      // how the count model itself learns the round is over -- but the
      // state machine's "round narration done" signal is onRoundComplete
      // (see below), fired only after the last tap's voice + animation.
      const value = model.increment();
      if (value === null) {
        sprite._reacting = false;
        if (idleManager && typeof idleManager.resumeSprite === 'function') {
          idleManager.resumeSprite(index);
        }
        return;
      }
      const isLastCount = value === model.getObjectCount();

      if (idleManager && typeof idleManager.pauseSprite === 'function') {
        idleManager.pauseSprite(index);
      }

      // Per-sprite resting position.  When the sprite's inner is
      // flipped (e.g. native-left asset walked to the right during
      // entrance), mirror the plaque's x so it lands on the
      // visually-correct side of the animal.
      const inner = sprite._inner;
      const flipped = !!(inner && inner.scale && inner.scale.x < 0);
      const spriteW = sprite._spriteW ?? 0;
      const finalX = mirrorPlaqueXForFlip(baseFinalX, spriteW, flipped);

      // --- Voice playback ----------------------------------------------
      // Resolve the URL now (while `value` is the snapshot for this tap),
      // but defer playback until the plaque entrance animation completes.
      // This mirrors legacy `NumberPlaqueLayer.animationDidFinish`, which
      // invokes `playSoundForNumber` only after the entrance animation
      // finishes -- i.e. once the numeral is actually visible on the
      // plaque.  For animationSequence animators (sheep/apples/flowers),
      // the text is hidden during the keyframe cycle and revealed at the
      // end; for zoomOut/zoomRotate (chicks/cows/horses), the plaque has
      // reached its resting scale so the numeral is fully readable.
      const voiceUrl = resolveNumber(
        voiceSetsData, locale, voiceSet, value, numeralScript,
      );

      // --- Plaque creation + entrance animation -------------------------
      let plaque;
      try {
        plaque = createNumberPlaque({
          theme,
          value,
          textureMap: plaqueTextureMap,
          numeralScript,
        });
      } catch (err) {
        if (import.meta.env?.DEV) {
          // eslint-disable-next-line no-console
          console.warn('[learn-tap] createNumberPlaque failed:', err);
        }
        sprite._reacting = false;
        if (idleManager && typeof idleManager.resumeSprite === 'function') {
          idleManager.resumeSprite(index);
        }
        return;
      }

      // Start the plaque hidden until the animator positions it,
      // so the first rendered frame doesn't flash it at (0, 0).
      plaque.visible = false;
      if (typeof sprite.addChild === 'function') sprite.addChild(plaque);
      sprite._plaque = plaque;

      const cancel = { cancelled: false };
      inFlight.add(cancel);

      // Decide the plaque/reaction coordination based on the legacy
      // `displayPlaqueReactionConfig` shape:
      //
      //   simultaneous      -- reaction + plaque entrance fire together
      //                        (apples: worriedApple face morphs as the
      //                         worm-plaque pops).
      //   timeFromStart     -- reaction fires immediately, plaque entrance
      //                        is delayed by `sequenceTimeValue` seconds
      //                        (flowers: flower opens up for 1.3s, then
      //                         the plaque grows out of its centre).
      //   (anything else)   -- plaque runs, reaction skipped.
      //
      // Preconditions for running a reaction: theme declares
      // plaqueReaction, AND the app threaded the reaction assets
      // (objectReactions + reactionTextureMap) through.  Absent either,
      // we fall back to plaque-only so the theme still works.
      const seq = reactionCfg?.sequence;
      const canRunReaction = reactionCfg && objectReactions && reactionTextureMap
        && (seq === 'simultaneous' || seq === 'timeFromStart');

      const startEntrance = () => animatePlaqueEntrance(plaque, {
        cfg,
        finalX,
        finalY,
        plaqueAnimations,
        textureMap: plaqueTextureMap,
        cancel,
      });

      // Fire the verbal number sound at the moment the numeral is
      // revealed on the plaque (i.e. right after the entrance animation
      // completes).  Guarded so a scene teardown or animation
      // cancellation mid-flight doesn't produce a stray voice-over.
      // Legacy parallel: NumberPlaqueLayer.animationDidFinish ->
      // GameSoundManager.playSoundForNumber.
      //
      // We AWAIT the voice promise so the chain passed to Promise.all
      // below doesn't resolve until the number has finished speaking.
      // That keeps the state machine's "Good counting!" praise from
      // stepping on the final tap's number ("...three!") on last tap.
      const playVoiceOnReveal = async () => {
        if (cancel.cancelled || destroyed) return;
        if (!voiceUrl) return;
        // "Verbal Prompts" setting gates the number voice.  When off,
        // the plaque still animates in and the count still advances;
        // only the voice-over is silenced.  Probed at call time (not
        // at manager-construction time) so toggling the setting
        // between rounds takes effect on the next tap.
        if (!verbalOn()) return;
        try {
          await playOneShot(voiceUrl);
        } catch { /* never let audio errors sink the scene */ }
      };

      // Chain the voice playback onto the entrance so every coordination
      // mode (simultaneous, timeFromStart, no-reaction) plays the voice
      // at the same logical moment: plaque entrance complete.
      const startEntranceAndSpeak = () =>
        startEntrance().then(playVoiceOnReveal);

      let plaquePromise;
      let reactionPromise;

      if (canRunReaction && seq === 'timeFromStart') {
        // Reaction animates on the sprite immediately; plaque entrance
        // is fired off by a setTimeout matching legacy NSTimer
        // coordination.  If the scene is torn down (cancel.cancelled)
        // before the delay elapses, the entrance is skipped.
        reactionPromise = animatePlaqueReaction(sprite, {
          reactionCfg,
          objectReactions,
          reactionTextureMap,
          cancel,
        });
        const delayMs = Math.max(0, (reactionCfg.sequenceTimeValue || 0) * 1000);
        plaquePromise = new Promise((resolve) => {
          setTimeout(() => {
            if (cancel.cancelled || destroyed) { resolve(); return; }
            startEntranceAndSpeak().then(resolve, resolve);
          }, delayMs);
        });
      } else if (canRunReaction && seq === 'simultaneous') {
        plaquePromise = startEntranceAndSpeak();
        reactionPromise = animatePlaqueReaction(sprite, {
          reactionCfg,
          objectReactions,
          reactionTextureMap,
          cancel,
        });
      } else {
        plaquePromise = startEntranceAndSpeak();
        reactionPromise = Promise.resolve();
      }

      Promise.all([
        plaquePromise.catch(() => { /* swallow per-promise */ }),
        reactionPromise.catch(() => { /* swallow per-promise */ }),
      ]).finally(() => {
        inFlight.delete(cancel);
        // Keep _counted set (the count persists); only clear
        // _reacting so idle can resume.
        sprite._reacting = false;
        if (!destroyed && idleManager && typeof idleManager.resumeSprite === 'function') {
          idleManager.resumeSprite(index);
        }
        // Fire the round-narration-complete callback AFTER everything
        // for the final tap has finished: plaque entrance animation,
        // the verbal number sound, and any sprite-side reaction.  Skip
        // it if the scene was torn down mid-flight so we don't wake a
        // state machine that's already been stopped.
        if (isLastCount && !cancel.cancelled && !destroyed && typeof onRoundComplete === 'function') {
          try { onRoundComplete(); } catch { /* swallow */ }
        }
      });
    };

    if (typeof sprite.on === 'function') {
      sprite.on('pointerdown', handler);
    }
    handlers.push({ sprite, handler });
  }

  // Replay any taps that landed before the manager was constructed
  // (pending-tap tracking during the post-entrance preload gap).  Use
  // the sprite's registered handler directly so we mirror real-tap
  // semantics: _counted guard, per-sprite flip detection, etc.
  if (pendingSprites) {
    let replayed = 0;
    for (const { sprite, handler } of handlers) {
      const hit = typeof pendingSprites.has === 'function'
        ? pendingSprites.has(sprite)
        : [...pendingSprites].includes(sprite);
      if (hit) {
        replayed += 1;
        try { handler(); } catch { /* handler guards internally */ }
      }
    }
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.log(`[learn-tap] pending-sprite replay: ${replayed}/${
        typeof pendingSprites.size === 'number' ? pendingSprites.size : [...pendingSprites].length
      } taps replayed`);
    }
  }

  return {
    getCount() { return model.getCount(); },
    destroy() {
      destroyed = true;
      // If we created the count model ourselves, shut it down so any
      // late-arriving calls (e.g. reentrant replay after destroy) are
      // rejected.  An injected model is the caller's responsibility.
      if (!countModel) model.destroy();
      for (const token of inFlight) token.cancelled = true;
      inFlight.clear();
      for (const { sprite, handler } of handlers) {
        if (typeof sprite.off === 'function') {
          sprite.off('pointerdown', handler);
        }
        // Clean up per-sprite state so a subsequent scene build
        // starting from the same reused objects (unlikely today --
        // scene.destroy() throws them away -- but cheap insurance)
        // doesn't see stale flags.
        sprite._reacting = false;
        if (sprite._plaque) {
          if (typeof sprite.removeChild === 'function') {
            try { sprite.removeChild(sprite._plaque); } catch { /* no-op */ }
          }
          sprite._plaque = null;
        }
      }
      handlers.length = 0;
    },
  };
}
